import React from 'react';
import { motion } from 'framer-motion';
import { Workflow, Lightbulb, Map, Combine, Users, Sparkles, LineChart } from 'lucide-react';
const elements = [{
  icon: Workflow,
  title: 'Exec-Scrum "Stand-Up" Relays',
  description: 'Tightly choreographed leadership rituals — including value harmonization sprints, shareholder clarity checkpoints, and constituent feedback loops — that keep the executive team in lockstep through critical transitions.'
}, {
  icon: Lightbulb,
  title: 'Technology Exposure & Assessments',
  description: 'Small-group forums where we present an up-to-the-minute view of machine learning, agentic decision-making, and cognitive workflow tools relevant to your key processes — proliferating exposure and reference points across the organization.'
}, {
  icon: Map,
  title: 'Stakeholder Mapping & Risk Profiling',
  description: 'Identify natural constituencies and adversaries to new workflow tools, spotlight implementation fault lines, and minimize political drag before it surfaces.'
}, {
  icon: Combine,
  title: 'Organizational Synergy & Transition Planning',
  description: 'Rationalize and document synergies, facilitate launch and communications plans, and develop a robust personnel and leadership transition roadmap — with focus on talent retention, role clarity, growth, and business continuity.'
}, {
  icon: Users,
  title: 'Targeted Leadership Workshops',
  description: 'Convene focused working groups around specific pivot points with cross-functional representation, accelerating solution design and organizational buy-in.'
}, {
  icon: Sparkles,
  title: 'Early Wins',
  description: 'Predict and promote "success harbingers" that build credibility, signal momentum, and sustain engagement through the harder middle work of transformation.'
}, {
  icon: LineChart,
  title: 'Measure, Report, Iterate',
  description: 'Establish integration KPIs, rigorously track financial, operational, and talent progress, and continually refine integration tactics for ongoing improvement.'
}];
// Stair-step pattern for the first three steps only
const stairPattern = [0, 1, 2];
const overlapClass = ['', 'md:-mt-72 lg:-mt-80', 'md:-mt-72 lg:-mt-80'];
// Shared step content renderer
function StepContent({
  el,
  index
}: {
  el: (typeof elements)[number];
  index: number;
}) {
  const Icon = el.icon;
  const stepNum = String(index + 1).padStart(2, '0');
  return <>
      <div className="flex items-center gap-3 mb-3" data-id="element-43">
        <div className="hidden md:flex w-10 h-10 rounded-full bg-navy border border-amber/40 items-center justify-center flex-shrink-0" data-id="element-44">
          <Icon size={16} className="text-amber" strokeWidth={1.75} data-id="element-45" />
        </div>
        <span className="text-xs font-medium tracking-[0.2em] uppercase text-amber" data-id="element-46">
          Step {stepNum}
        </span>
      </div>
      <h4 className="text-2xl md:text-[1.65rem] font-serif text-offwhite leading-tight mb-3" data-id="element-47">
        {el.title}
      </h4>
      <p className="text-base text-offwhite-muted font-light leading-relaxed max-w-md" data-id="element-48">
        {el.description}
      </p>
      {/* Mobile-only icon node on left rail */}
      <div className="md:hidden absolute left-0 top-0" data-id="element-49">
        <div className="w-10 h-10 rounded-full bg-navy border border-amber/40 flex items-center justify-center" data-id="element-50">
          <Icon size={16} className="text-amber" strokeWidth={1.75} data-id="element-51" />
        </div>
      </div>
    </>;
}
function StepMotion({
  el,
  index,
  className
}: {
  el: (typeof elements)[number];
  index: number;
  className?: string;
}) {
  return <motion.li initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.55,
    ease: 'easeOut',
    delay: index * 0.04
  }} className={`relative pl-14 md:pl-0 ${className ?? ''}`} data-id="element-52">
      <StepContent el={el} index={index} data-id="element-53" />
    </motion.li>;
}
export function Features() {
  const stairSteps = elements.slice(0, 3);
  const rowSteps = elements.slice(3, 6);
  const finalStep = elements[6];
  return <section id="services" className="pt-4 pb-24 md:pt-6 md:pb-32 relative overflow-hidden" data-id="element-54">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-55">
        {/* Methodology Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20" data-id="element-56">
          <div className="lg:col-span-5" data-id="element-57">
            <p className="text-sm font-medium tracking-wider uppercase text-amber mb-4" data-id="element-58">
              Our Methodology
            </p>
            <h2 className="text-4xl md:text-5xl font-serif leading-tight" data-id="element-59">
              The Fulcrum Transition Method
              <sup className="text-amber text-2xl md:text-3xl font-sans align-super ml-0.5" data-id="element-60">
                ™
              </sup>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4" data-id="element-61">
            <p className="text-lg text-offwhite-muted font-light leading-relaxed mb-6" data-id="element-62">
              We identify executive objectives, surface political and
              operational fault lines, and establish a{' '}
              <span className="text-offwhite italic" data-id="element-63">business fulcrum</span> — a
              shared framework of metrics, decision logic, and governance
              principles capable of exerting sustained leverage across the
              organization.
            </p>
            <p className="text-lg text-offwhite-muted font-light leading-relaxed" data-id="element-64">
              Our approach respects executive bandwidth. We drive alignment
              around shared value in{' '}
              <span className="text-amber" data-id="element-65">days — not months</span> — empowering
              leadership to lead, not wrangle.
            </p>
          </div>
        </div>

        {/* Steps 1–3: Stair-step progression */}
        <ol className="relative flex flex-col max-w-5xl mb-16 md:mb-20" data-id="element-66">
          {stairSteps.map((el, index) => <motion.li key={el.title} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.55,
          ease: 'easeOut',
          delay: index * 0.04
        }} className={`relative md:grid md:grid-cols-3 ${overlapClass[index]}`} data-id="element-67">
              <div className={`pl-14 md:pl-0 ${stairPattern[index] === 0 ? 'md:col-start-1' : stairPattern[index] === 1 ? 'md:col-start-2' : 'md:col-start-3'} md:pr-6`} data-id="element-68">
                <StepContent el={el} index={index} data-id="element-69" />
              </div>
            </motion.li>)}
        </ol>

        {/* Steps 4, 5, 6: straight row */}
        <ol className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 mb-12 md:mb-16" data-id="element-70">
          {rowSteps.map((el, i) => <StepMotion key={el.title} el={el} index={i + 3} className="md:pr-6" data-id="element-71" />)}
        </ol>

        {/* Step 7: below step 4, extends to middle of page */}
        <ol className="relative grid grid-cols-1 md:grid-cols-2 gap-10" data-id="element-72">
          <StepMotion el={finalStep} index={6} className="md:pr-6" data-id="element-73" />
        </ol>

        {/* Mobile rail line */}
        <div className="md:hidden absolute left-[2.625rem] top-0 bottom-0 w-px bg-gradient-to-b from-amber/0 via-amber/25 to-amber/0 pointer-events-none" aria-hidden="true" data-id="element-74" />
      </div>
    </section>;
}